import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  ColorMap,
  periodisationLabel,
  resolvePeriodisationColor,
} from "../../utils/scheduleColors";
import { getLocalDateString, isSameDay, startOfToday } from "./scheduleDateUtils";
import { EventVM } from "./eventVM";
import { eventTypeLabel } from "../../types/event";

interface YearViewProps {
  year: number;
  /** Returns the periodisation phase for a given date, if any. */
  getPhaseForDate: (date: Date) => string | undefined;
  /** Events on a day → a dot in the cell (Events.md §4). */
  getEventsForDate?: (date: Date) => EventVM[];
  /** Distinct phases present in the year (drives the legend). */
  phasesPresent: string[];
  periodisationColors?: ColorMap;
  onPrevYear: () => void;
  onNextYear: () => void;
  onSelectDay: (date: Date) => void;
}

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const MiniMonth: React.FC<{
  year: number;
  monthIndex: number;
  getPhaseForDate: (date: Date) => string | undefined;
  getEventsForDate?: (date: Date) => EventVM[];
  periodisationColors?: ColorMap;
  onSelectDay: (date: Date) => void;
}> = ({
  year,
  monthIndex,
  getPhaseForDate,
  getEventsForDate,
  periodisationColors,
  onSelectDay,
}) => {
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const today = startOfToday();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push(new Date(year, monthIndex, d));
  }

  return (
    <div>
      <h4 className="mb-1.5 text-sm font-semibold text-base-content font-grotesk">
        {firstDay.toLocaleDateString("en-GB", { month: "long" })}
      </h4>
      <div className="grid grid-cols-7 gap-y-0.5">
        {WEEKDAYS.map((d, i) => (
          <div
            key={i}
            className="text-center text-[9px] font-medium text-base-content/30"
          >
            {d}
          </div>
        ))}
        {cells.map((day, index) => {
          if (!day) return <div key={`e-${index}`} className="h-5" />;
          const phase = getPhaseForDate(day);
          const event = getEventsForDate?.(day)[0];
          const isToday = isSameDay(day, today);
          const bg = phase
            ? resolvePeriodisationColor(phase, periodisationColors)
            : undefined;
          const title = [
            phase ? periodisationLabel(phase) : null,
            event ? `${event.name} · ${eventTypeLabel(event.type)}` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <button
              key={getLocalDateString(day)}
              onClick={() => onSelectDay(day)}
              title={title || undefined}
              className={`relative flex h-5 items-center justify-center text-[10px] transition-transform hover:scale-110 ${
                isToday ? "ring-1 ring-base-content rounded" : ""
              }`}
              style={
                bg
                  ? {
                      backgroundColor: bg,
                      color: "#ffffff",
                    }
                  : undefined
              }
            >
              {day.getDate()}
              {event && (
                <span
                  aria-hidden
                  className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-base-100"
                  style={{ backgroundColor: event.color }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const YearView: React.FC<YearViewProps> = ({
  year,
  getPhaseForDate,
  getEventsForDate,
  phasesPresent,
  periodisationColors,
  onPrevYear,
  onNextYear,
  onSelectDay,
}) => {
  return (
    <div>
      {/* Year navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onPrevYear}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-base-300/60 text-base-content hover:bg-base-300 transition-colors"
          aria-label="Previous year"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <h3 className="text-base font-semibold text-base-content font-grotesk">
          {year}
        </h3>
        <button
          onClick={onNextYear}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-base-300/60 text-base-content hover:bg-base-300 transition-colors"
          aria-label="Next year"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Legend */}
      {phasesPresent.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {phasesPresent.map((phase) => (
            <div key={phase} className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{
                  backgroundColor: resolvePeriodisationColor(
                    phase,
                    periodisationColors,
                  ),
                }}
              />
              <span className="text-xs text-base-content/60 font-grotesk">
                {periodisationLabel(phase)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-3">
        {Array.from({ length: 12 }, (_, m) => (
          <MiniMonth
            key={m}
            year={year}
            monthIndex={m}
            getPhaseForDate={getPhaseForDate}
            getEventsForDate={getEventsForDate}
            periodisationColors={periodisationColors}
            onSelectDay={onSelectDay}
          />
        ))}
      </div>

      {phasesPresent.length === 0 && (
        <p className="mt-6 text-center text-sm text-base-content/50 font-grotesk">
          No periodised programs scheduled this year. Assign or activate a
          program to see your training phases.
        </p>
      )}
    </div>
  );
};

export default YearView;
